/**
 * Long-form copy for the renewal-concierge flows, taken verbatim from
 * docs/renewal-consierge/renewal-consierge-updates.md.
 *
 * Kept here rather than in the locale files while this is a click-through
 * prototype: the wording is still being reviewed, and duplicating several
 * hundred words into en + es before it settles would make revisions painful.
 * Move into `translation.json` when the copy is signed off and the backend
 * lands.
 */

export const SDA_DISCLAIMER = {
  title: "About Your Security Deposit Alternative with Lighthouse",
  body: "Your Security Deposit Alternative with Lighthouse provides your property manager financial protection on your behalf in the event you default on your lease, or damage the property beyond normal wear and tear. You remain responsible for satisfying the terms of your lease, and must maintain coverage at all times. To maintain coverage, you pay a smaller, non-refundable fee once per lease term. If you leave a balance at move-out, a claim may be filed against your coverage, and if approved, you will be responsible for reimbursing Lighthouse, up to your coverage limit.",
};

export const BILLING_INFO = {
  title: "Renewing with Lighthouse",
  body: "Your Security Deposit Alternative with Lighthouse is billed automatically, once per lease term, using your default payment method on file. You can update this information at any time, however a verified bank account must always remain on file.",
};

/**
 * Lead-in for the emphasised disclaimers (the 30-day notice on every Review
 * step, and the renewal-offer disclaimer on the Preference step). Kept
 * separate from the sentences so callers can bold it without parsing strings.
 */
export const IMPORTANT_LEAD = "IMPORTANT:";

export const NOTICE_30_DAY =
  "Most leases require you provide a minimum 30-day's notice to vacate. If you are within this window, or need to change anything after submitting, contact your property manager directly as soon as possible.";

/**
 * Split around "before" so the component can bold it — the word is the whole
 * point of the notice (early vs late), and it sits mid-sentence.
 */
export const EARLY_MOVE_OUT_NOTICE = {
  before: "You've indicated you plan to move out ",
  emphasis: "before",
  after:
    " your lease end date. You may still be responsible for additional fees through the end of your lease. For more information, please contact your property manager directly.",
};

/** Split around "after" for the same reason as the early notice above. */
export const LATE_MOVE_OUT_NOTICE = {
  before: "You've indicated you plan to move out ",
  emphasis: "after",
  after:
    " your lease expires. Any extension may require prior written approval from your property manager and could require additional payment, including renewing your Security Deposit Alternative with Lighthouse. For more information, please contact your property manager directly.",
};

/** Split around "REQUEST" so the component can bold it. */
export const NOT_SURE_DISCLAIMER = {
  before: "This is a ",
  emphasis: "REQUEST",
  after:
    " for a renewal offer, only. This is not a formal offer, or guarantee of any terms. Your property manager will follow up with you directly.",
};

/** "Did You Know?" moving-cost breakdown on the move-out date step. */
export const MOVING_COSTS = {
  // Split so the modal can render "Did you know?" as a small eyebrow above
  // the headline rather than one long run-on line.
  eyebrow: "Did you know?",
  title: "Moving often costs more than most people expect.",
  hardCostsTitle: "The hard costs add up fast:",
  hardCosts: [
    { label: "Movers or truck rental", value: "$200–$2,500" },
    { label: "New security deposit", value: "1–2x Monthly Rent" },
    { label: "Application fees at your new place", value: "$50–$100 Per Applicant" },
    { label: "Utility setup and deposits", value: "$150–$500" },
    { label: "Move-out cleaning", value: "$250–$1,000" },
  ],
  // Excludes the security deposit (quoted as a multiple of rent, so it can't
  // be added to a flat range) and, as the copy says, the new rent itself.
  // The hard-cost figures above sum to roughly $650–$4,100.
  summary:
    "All in, a typical move runs $1,500–$5,000 — before your new rent is even factored in.",
  hiddenCostsTitle: "Other typical costs that catch people off guard:",
  hiddenCosts: [
    {
      label: "Overlap Rent",
      detail: "Your move-out and move-in dates don't line up",
    },
    {
      label: "Lost Wages",
      detail: "Moving day(s), utility appointment(s), in-person walkthroughs, etc.",
    },
    {
      label: "Deposit Disputes",
      detail: "Normal wear and tear vs. damages",
    },
  ],
  reconsider:
    "Before you go — have you talked to your property manager about renewing? It might cost less than you think to stay.",
  disclaimer:
    "Prices shown are examples only, based on national averages. Actual amounts may vary.",
};

export const PRICES_DISCLAIMER =
  "Prices shown are examples only — actual amounts may vary.";

/** Expandable sections under "Tips for Avoiding Fees at Move-Out". */
export const MOVE_OUT_TIPS = {
  intro:
    "Common charges at move-out and how to avoid them. Your lease may have specific requirements at move-out. For more information, please speak with your property manager directly.",
  sections: [
    {
      title: "Cleaning",
      items: [
        { label: "Full unit deep clean", value: "$200–$400" },
        { label: "Carpet cleaning", value: "$150–$300" },
        { label: "Appliance cleaning", value: "$50–$150" },
      ],
      tip: "Clean appliances inside and out, wipe down baseboards, and leave the unit broom-swept at minimum.",
    },
    {
      title: "Damage & Repairs",
      items: [
        { label: "Wall patching & paint", value: "$100–$400" },
        { label: "Carpet replacement", value: "$300–$1,200" },
        { label: "Door / blind replacement", value: "$75–$250" },
      ],
      tip: "Patch small nail holes and report anything broken before your walkthrough so it isn't a surprise.",
    },
    {
      title: "Removal & Disposal",
      items: [
        { label: "Furniture / junk removal", value: "$100–$500" },
        { label: "Trash left behind", value: "$50–$200" },
      ],
      tip: "Remove everything, including items in storage areas, patios, and garages.",
    },
    {
      title: "Keys & Access",
      items: [
        { label: "Lock rekey", value: "$50–$150" },
        { label: "Unreturned remote / fob", value: "$25–$100" },
      ],
      tip: "Return every key, fob, and remote you were given — including copies you made.",
    },
  ],
};

/** "How Security Deposits & Deposit Alternatives Work at Move-Out". */
export const DEPOSIT_EXPLAINER = {
  title: "How Security Deposits & Deposit Alternatives Work at Move-Out",
  paragraphs: [
    "After you vacate, your property manager will conduct a move-out inspection and compare against the unit's condition at move-in (often documented on an inspection report).",
    "Normal wear and tear, such as minor scuffs, small nail holes, or gradual carpet wear, is generally not considered chargeable. Damage beyond normal wear & tear is typically deductible. For additional clarification, please speak with your property manager directly before vacating.",
    "If you used a Security Deposit Alternative, such as Lighthouse, the property manager may file a claim for reimbursement against your policy for any unpaid rent and/or damages beyond normal wear & tear. If a claim is approved, and paid on your behalf, you will be responsible for reimbursing the provider (i.e. Lighthouse) up to your coverage limit. Payment plans may be made available.",
  ],
  // Lead-in kept separate so the component can bold it, matching the
  // per-section tips.
  proTipLead: "PRO TIP:",
  proTip:
    "Communicate early & often with your property manager when you plan on moving out. Ask them for clear instructions on what they expect when you hand over the keys, and request it in writing.",
};

/** `subtitle` is optional — "6 Months" is deliberately shown without one. */
export const PREFERENCE_OPTIONS: {
  id: "12-month" | "6-month" | "month-to-month" | "undecided";
  title: string;
  subtitle?: string;
  /** Renders as a small pill beside the title. */
  tag?: string;
}[] = [
  {
    id: "12-month",
    title: "12 Months",
    subtitle: "Best option for minimizing rent increases",
    tag: "Most Popular",
  },
  {
    id: "6-month",
    title: "6 Months",
  },
  {
    id: "month-to-month",
    title: "Month-to-Month",
    subtitle: "Typically comes with higher monthly rents",
  },
  {
    id: "undecided",
    title: "I'm Not Sure Yet",
    subtitle: "Your PM will reach out to discuss your options",
  },
];

export const NOTES_PROMPT = {
  title: "Anything else to share?",
  subtitle: "We'll pass along the message to your property manager.",
};
